import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { PaginateListMore } from 'src/app/common/types/common-types';
import { useDataProvider } from 'src/app/common/utils';
import { ParamsProps } from '../../../../../common/components/ParamsProvider';
import PruFilter, { PruFilterItemType } from '../../../../../common/components/PruTable/PruFilter';
import PruTable from '../../../../../common/components/PruTable/PruTable';
import { getDayEnd, getDayStart, getFirstDayOfMonth } from '../../../../../common/utils/time-utils';
import {
  fetchCandidateRecruitmentPipelineReport,
  fetchCandidateRecruitmentPipelineReportExport,
} from '../../../network/candidate-recruitment-pipeline-crud';
import { ReportItem, ReportListParams, SOURCE_OPTIONS } from '../../../types/candidate-recruitment-pipeline';

type NewRegisteredCandidateReportProps = {} & ParamsProps;

const initialState: ReportListParams = {
  createdAtFrom: getFirstDayOfMonth(new Date()),
  createdAtTo: getDayEnd(new Date()),
  agentCode: '',
  leaderCode: '',
  unit: '',
  candidateName: '',
  phone: '',
  source: '',
  page: 1,
  limit: 5,
};

const paramsInitiator = (initialParams?: Record<string, string>): ReportListParams => {
  return initialParams
    ? {
        createdAtFrom: initialParams.createdAtFrom
          ? getDayStart(new Date(initialParams.createdAtFrom))
          : getFirstDayOfMonth(new Date()),
        createdAtTo: initialParams.createdAtTo ? getDayEnd(new Date(initialParams.createdAtTo)) : getDayEnd(new Date()),
        agentCode: '',
        leaderCode: '',
        unit: '',
        candidateName: '',
        phone: '',
        source: '',
        page: 1,
        limit: 5,
      }
    : initialState;
};

const CandidateRecruitmentPipelineReport: FC<NewRegisteredCandidateReportProps> = ({ initialParams }) => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();
  const defaultFilterValue = paramsInitiator(initialParams);

  const [filterState, setFilterState] = useState<ReportListParams>(defaultFilterValue);
  const [reportList, setReportList] = useState<PaginateListMore<ReportItem>>();
  const { isLoading, refreshData } = useDataProvider<PaginateListMore<ReportItem>>(
    async () => {
      try {
        return await fetchCandidateRecruitmentPipelineReport(filterState, dispatch);
      } catch (err) {}
    },
    setReportList,
    false,
  );

  const fetchCsvtDownload = () => {
    try {
      fetchCandidateRecruitmentPipelineReportExport(filterState, dispatch);
    } catch (err) {}
  };

  return (
    <>
      <PruFilter
        title="Candidate Recruitment Pipeline Report Filters"
        itemDef={[
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'createdAtFrom',
            fieldTo: 'createdAtTo',
            initialDateFrom: filterState.createdAtFrom,
            initialDateTo: filterState.createdAtTo,
            defaultDateFrom: defaultFilterValue.createdAtFrom,
            defaultDateTo: defaultFilterValue.createdAtTo,
            displayName: 'Register Date',
            required: true,
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'agentCode',
            initialValue: filterState.agentCode,
            displayName: 'Agent Code',
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'leaderCode',
            initialValue: filterState.leaderCode,
            displayName: 'Leader Code',
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'unit',
            initialValue: filterState.unit,
            displayName: 'Unit',
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'candidateName',
            initialValue: filterState.candidateName,
            displayName: 'Candidate Name',
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'phone',
            initialValue: filterState.phone,
            displayName: 'Phone Number',
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'source',
            initialValue: filterState.source,
            displayName: 'Source',
            choices: SOURCE_OPTIONS,
          },
        ]}
        onChangeFilter={(data) =>
          setFilterState({
            ...filterState,
            agentCode: data.agentCode,
            leaderCode: data.leaderCode,
            unit: data.unit,
            candidateName: data.candidateName,
            phone: data.phone,
            source: data.source,
            createdAtFrom: data.createdAtFrom,
            createdAtTo: data.createdAtTo,
          })
        }
        fetchData={refreshData}
      />
      <PruTable
        style={{ wordBreak: 'keep-all' }}
        disableBulkSelect
        dataSource={reportList?.items}
        isLoading={isLoading}
        onRefresh={refreshData}
        title={Translation('report.recruitment.newRegisteredCandidate.list')}
        headerBtnDef={[
          {
            color: 'primary',
            title: Translation('report.common.export'),
            onClick: () => fetchCsvtDownload(),
          },
        ]}
        operationDef={[]}
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: '_id',
            displayName: '',
            renderData: (row) => row._id || '-',
          },
          {
            keyIndex: 'candidateName',
            displayName: 'Candidate Name',
            renderData: (row) => row.fullName || '-',
            style: {
              minWidth: 200,
            },
          },
          {
            keyIndex: 'phone',
            align: 'center',
            displayName: 'Phone Number',
            renderData: (row) => row.phone || '-',
            style: {
              minWidth: 200,
            },
          },
          {
            keyIndex: 'source',
            align: 'center',
            displayName: 'Source',
            renderData: (row) => row.source || '-',
            style: {
              minWidth: 200,
            },
          },
          {
            keyIndex: 'created_time',
            align: 'center',
            displayName: 'Candidate Create Time',
            renderData: (row) => row.created_time || '-',
            style: {
              minWidth: 200,
            },
          },
          {
            keyIndex: 'agentidShare',
            align: 'center',
            displayName: 'Shared by AL/AG Code',
            renderData: (row) => row.agentShare || '-',
            style: {
              minWidth: 200,
            },
          },
          {
            keyIndex: 'agentNameShare',
            displayName: 'Shared by AL/AG Name',
            renderData: (row) => row.agentShareName || '-',
            style: {
              minWidth: 200,
            },
          },
          {
            keyIndex: 'agentid',
            align: 'center',
            displayName: 'AL Code',
            renderData: (row) => row.agentid || '-',
            style: {
              minWidth: 200,
            },
          },
          {
            keyIndex: 'agentLeaderName',
            displayName: 'AL Name',
            renderData: (row) => row.agentLeaderName || '-',
            style: {
              minWidth: 200,
            },
          },
          {
            keyIndex: 'unitCode',
            align: 'center',
            displayName: 'Unit Code',
            renderData: (row) => row.unit || '-',
            style: {
              minWidth: 100,
            },
          },
          {
            keyIndex: 'timeContact',
            align: 'center',
            displayName: 'Contacted Time',
            renderData: (row) => row.timeContact || '-',
            style: {
              minWidth: 200,
            },
          },
          {
            keyIndex: 'appointmentSetTime',
            align: 'center',
            displayName: 'Appt Set Time',
            renderData: (row) => row.appointmentSetTime || '-',
            style: {
              minWidth: 200,
            },
          },
          {
            keyIndex: 'attendedMeetingTime',
            align: 'center',
            displayName: 'Appt Seen Time',
            renderData: (row) => row.attendedMeetingTime || '-',
            style: {
              minWidth: 200,
            },
          },
          {
            keyIndex: 'checkinPocTime',
            align: 'center',
            displayName: 'POC Check-in Time',
            renderData: (row) => row.checkinPocTime || '-',
            style: {
              minWidth: 200,
            },
          },
          {
            keyIndex: 'checkinBopTime',
            align: 'center',
            displayName: 'BOP Check-in Time',
            renderData: (row) => row.checkinBopTime || '-',
            style: {
              minWidth: 200,
            },
          },
          {
            keyIndex: 'nbriTime',
            align: 'center',
            displayName: 'NBRI Time',
            renderData: (row) => row.nbriTime || '-',
            style: {
              minWidth: 200,
            },
          },
          {
            keyIndex: 'bop1_1checkinTime',
            align: 'center',
            displayName: 'BOP 1-1 Check-in Time',
            renderData: (row) => row.bop1_1checkinTime || '-',
            style: {
              minWidth: 200,
            },
          },
        ]}
        totalPages={reportList?.totalPages}
        totalRecords={reportList?.totalRecords}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            limit: rowsPerPage,
          });
          refreshData();
        }}
      />
    </>
  );
};

export default CandidateRecruitmentPipelineReport;
